import { useEffect, useState } from "react";
import { api, formatMoney } from "@/lib/api";
import CountrySwitcher from "@/components/CountrySwitcher";
import { useCountry } from "@/lib/country";
import PricingDisclaimer, { PricingNote } from "@/components/PricingDisclaimer";

const CATEGORY_LABELS = {
  business_setup: "Business Setup",
  business_presence: "Business Presence",
  business_growth: "Business Growth",
  investment_expansion: "Investment & Expansion",
  professional_referrals: "Professional Referrals",
};

export default function Services() {
  const { countrySlug } = useCountry();
  const [services, setServices] = useState([]);
  const [country, setCountry] = useState(null);

  useEffect(() => {
    api.get(`/services?country=${countrySlug}`).then((r) => setServices(r.data));
    api.get(`/countries/${countrySlug}`).then((r) => setCountry(r.data));
  }, [countrySlug]);

  const grouped = services.reduce((acc, s) => { (acc[s.category] = acc[s.category] || []).push(s); return acc; }, {});

  return (
    <div data-testid="services-page" className="fade-in py-20">
      <div className="container-gos">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <div className="overline mb-3">Catalog</div>
            <h1 className="font-display text-4xl sm:text-5xl text-gos-charcoal tracking-tight">Full service catalog</h1>
            <p className="text-gos-slate mt-4 max-w-2xl leading-relaxed">Browse every service we offer. Pricing updates instantly when you change country.</p>
          </div>
          <div>
            <div className="overline mb-2">Country</div>
            <CountrySwitcher />
          </div>
        </div>

        <PricingNote className="mt-8 max-w-3xl" />

        <div className="mt-14 space-y-14">
          {Object.entries(grouped).map(([cat, items]) => (
            <div key={cat}>
              <div className="overline text-gos-navy mb-4">{CATEGORY_LABELS[cat] || cat}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map((s) => (
                  <div key={s.slug} data-testid={`service-card-${s.slug}`} className="bg-white border border-gos-border p-6 rounded-sm card-lift">
                    <div className="font-display text-lg text-gos-navy">{s.name}</div>
                    <p className="text-xs text-gos-slate mt-2 leading-relaxed">{s.description}</p>
                    <div className="mt-4 border-t border-gos-border pt-4 font-mono text-sm flex justify-between">
                      <div>
                        <div className="text-[10px] text-gos-muted uppercase tracking-widest">One-time</div>
                        <div className="text-gos-charcoal">{s.price?.one_time ? formatMoney(s.price.one_time, country?.currency_code || "CAD") : "—"}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] text-gos-muted uppercase tracking-widest">Monthly</div>
                        <div className="text-gos-charcoal">{s.price?.monthly ? formatMoney(s.price.monthly, country?.currency_code || "CAD") : "—"}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 bg-gos-subtle border border-gos-border p-8 rounded-sm">
          <div className="overline">Immigration disclosure</div>
          <p className="text-sm text-gos-slate mt-2 leading-relaxed">
            Immigration-related services are provided through licensed immigration consultants and lawyers within our approved professional partner network. We coordinate introductions and communication support. Regulated immigration advice and representation are provided only by authorized professionals.
          </p>
        </div>

        <div className="mt-8">
          <PricingDisclaimer />
        </div>
      </div>
    </div>
  );
}
